import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { toast } from "sonner";
import { ArrowRight, Mail, CheckCircle2, LogIn, UserPlus } from "lucide-react";
import { PasswordField } from "../components/PasswordField";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { useNoIndex } from "../hooks/useNoIndex";

const AuthShell = ({ title, subtitle, children, footer, testid }) => {
  // All auth pages (login/register/forgot/reset) are noindex — no SEO value.
  useNoIndex();
  return (
  <div className="washi-bg min-h-screen" data-testid={testid}>
    <Navbar />
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-32">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">{subtitle}</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] text-[#1A2A40] mb-6">
          {title}
        </h1>
        <p className="text-lg text-[#4A5568] leading-relaxed max-w-md">
          Apprends le japonais en 15 min par jour, sans douleur, avec une méthode native et un tuteur IA.
          <span className="block mt-3 font-jp text-2xl text-[#E94A3F]">一歩ずつ。</span>
        </p>
      </div>
      <div className="lg:col-span-7">
        <div className="bg-[#FDFBF7] rounded-3xl p-8 sm:p-12 border-2 border-[#1A2A40] shadow-brut">
          {children}
          <div className="mt-8 pt-6 border-t border-[#1A2A40]/10 text-sm text-center text-[#4A5568]">
            {footer}
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
  );
};

/**
 * Prominent, symmetric switch banner shown at the TOP of Login and Register.
 * Ensures users always see the alternate path in one glance.
 */
const AltPathSwitch = ({ variant, email }) => {
  // variant: "to-login" (shown on Register) or "to-register" (shown on Login)
  const isToLogin = variant === "to-login";
  const to = isToLogin
    ? `/login${email ? `?email=${encodeURIComponent(email)}` : ""}`
    : "/register";
  const Icon = isToLogin ? LogIn : UserPlus;
  const label = isToLogin ? "Déjà un compte ?" : "Nouveau ici ?";
  const cta = isToLogin ? "Se connecter" : "Créer un compte";
  const testid = isToLogin ? "switch-to-login" : "switch-to-register";
  return (
    <Link
      to={to}
      data-testid={testid}
      className="mb-6 flex items-center justify-between gap-3 rounded-xl border-2 border-[#1A2A40]/15 bg-[#FFF5E0] px-4 py-3 hover:border-[#E94A3F] hover:bg-[#FFEACC] transition-colors group"
    >
      <span className="flex items-center gap-2 text-sm text-[#1A2A40]">
        <Icon className="w-4 h-4 text-[#E94A3F]" />
        <span><strong>{label}</strong> Passez par l'autre chemin en un clic.</span>
      </span>
      <span className="shrink-0 inline-flex items-center gap-1 text-sm font-bold text-[#E94A3F] group-hover:gap-2 transition-all">
        {cta} <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
};

export const LoginPage = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const prefilledEmail = params.get("email") || "";
  const [form, setForm] = useState({ email: prefilledEmail, password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Bon retour ! 🌸");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={<>Bon retour <span className="font-jp text-[#E94A3F]">お帰り</span></>} subtitle="Connexion" testid="login-page"
      footer={<>Pas encore de compte ? <Link to="/register" data-testid="footer-register-link" className="text-[#E94A3F] font-bold hover:underline">S'inscrire</Link></>}
    >
      <AltPathSwitch variant="to-register" />
      <div className="mb-5 space-y-3" data-testid="google-auth-block-login">
        <GoogleAuthButton testid="google-login-btn" />
        <div className="flex items-center gap-3 text-xs text-[#A0AEC0] uppercase tracking-wider">
          <span className="h-px flex-1 bg-[#1A2A40]/10" />
          <span>ou avec email</span>
          <span className="h-px flex-1 bg-[#1A2A40]/10" />
        </div>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Email</label>
          <input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="login-email-input"
            className="w-full bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#E94A3F]"
          />
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40]">Mot de passe</label>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-xs font-medium text-[#E94A3F] hover:underline"
            >
              Oublié ?
            </Link>
          </div>
          <PasswordField
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            testid="login-password-input"
            autoComplete="current-password"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full bg-[#E94A3F] text-white font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? "..." : (<>Se connecter <ArrowRight className="w-4 h-4" /></>)}
        </button>
      </form>
    </AuthShell>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  // When set, an inline "email already exists" callout appears with a clickable
  // action to jump straight to /login with the email pre-filled.
  const [existingEmail, setExistingEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Mot de passe : 6 caractères minimum"); return; }
    setLoading(true);
    setExistingEmail("");
    try {
      await register(form.name, form.email, form.password);
      toast.success("Bienvenue ! Direction le dashboard 🌸");
      nav("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      const status = err.response?.status;
      // Backend returns 400 + "Cet email est déjà inscrit" when the email already exists.
      const isDup = status === 400 && /déjà inscrit|already.*(registered|exists)/i.test(detail);
      if (isDup) {
        setExistingEmail(form.email);
      } else {
        toast.error(detail || "Échec de l'inscription");
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    nav(`/login?email=${encodeURIComponent(existingEmail || form.email)}`);
  };

  return (
    <AuthShell
      title={<>Commencez. <span className="italic font-light">Pas à pas.</span></>}
      subtitle="Inscription gratuite"
      testid="register-page"
      footer={<>Déjà inscrit ? <Link to="/login" data-testid="footer-login-link" className="text-[#E94A3F] font-bold hover:underline">Connexion</Link></>}
    >
      <AltPathSwitch variant="to-login" email={form.email} />
      <div className="mb-5 space-y-3" data-testid="google-auth-block-register">
        <GoogleAuthButton label="S'inscrire avec Google" testid="google-register-btn" />
        <div className="flex items-center gap-3 text-xs text-[#A0AEC0] uppercase tracking-wider">
          <span className="h-px flex-1 bg-[#1A2A40]/10" />
          <span>ou avec email</span>
          <span className="h-px flex-1 bg-[#1A2A40]/10" />
        </div>
      </div>
      {existingEmail && (
        <div
          data-testid="email-exists-callout"
          className="mb-5 rounded-xl border-2 border-[#E94A3F] bg-[#FFF5E0] p-4"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-8 h-8 rounded-full bg-[#E94A3F] text-white flex items-center justify-center">
              <LogIn className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1A2A40]">
                Cet email a déjà un compte.
              </p>
              <p className="text-sm text-[#4A5568] mt-1 break-words">
                <strong>{existingEmail}</strong> est déjà inscrit. Connectez-vous en un clic, l'email est déjà pré-rempli.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={goToLogin}
                  data-testid="email-exists-login-btn"
                  className="inline-flex items-center gap-2 bg-[#E94A3F] text-white text-sm font-bold px-4 py-2 rounded-lg shadow-brut-sm hover-lift"
                >
                  Se connecter <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/forgot-password"
                  data-testid="email-exists-forgot-link"
                  className="text-sm font-medium text-[#E94A3F] hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Prénom</label>
          <input
            type="text" required value={form.name} maxLength={80}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name-input"
            className="w-full bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#E94A3F]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Email</label>
          <input
            type="email" required value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); if (existingEmail) setExistingEmail(""); }}
            data-testid="register-email-input"
            className="w-full bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#E94A3F]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Mot de passe (min. 6)</label>
          <PasswordField
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            minLength={6}
            testid="register-password-input"
            autoComplete="new-password"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="register-submit-btn"
          className="w-full bg-[#E94A3F] text-white font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? "..." : (<>Créer mon compte <ArrowRight className="w-4 h-4" /></>)}
        </button>
        <p className="text-xs text-[#A0AEC0] text-center">Aucune carte bancaire requise. Désinscription en 1 clic.</p>
      </form>
    </AuthShell>
  );
};



// ----- Forgot password -----
export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Mot de passe <span className="italic font-light">oublié</span> ?</>}
      subtitle="Réinitialisation"
      testid="forgot-password-page"
      footer={<>Tu te souviens ? <Link to="/login" className="text-[#E94A3F] font-bold hover:underline">Connexion</Link></>}
    >
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-success">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#34D399]/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-[#34D399]" />
          </div>
          <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-2">Email envoyé !</h2>
          <p className="text-[#4A5568] leading-relaxed mb-4">
            Si un compte existe pour <strong>{email}</strong>, tu vas recevoir un email avec un lien sécurisé
            pour choisir un nouveau mot de passe.
          </p>
          <p className="text-sm text-[#A0AEC0]">
            Le lien expire dans <strong>1 heure</strong>. Pense à vérifier ton dossier <em>Spam</em> si tu ne reçois rien.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Entre l'email associé à ton compte. Nous t'enverrons un lien pour réinitialiser ton mot de passe.
          </p>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AEC0]" />
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="w-full bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-[#E94A3F]"
                placeholder="ton@email.com"
              />
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
            className="w-full bg-[#E94A3F] text-white font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? "Envoi…" : (<>M'envoyer un lien <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      )}
    </AuthShell>
  );
};


// ----- Reset password -----
export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Lien invalide. Demande un nouveau lien.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Mot de passe : 6 caractères minimum"); return; }
    if (password !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Mot de passe mis à jour ! 🌸");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Nouveau <span className="italic font-light">mot de passe</span></>}
      subtitle="Réinitialisation"
      testid="reset-password-page"
      footer={<>Tu te souviens finalement ? <Link to="/login" className="text-[#E94A3F] font-bold hover:underline">Connexion</Link></>}
    >
      {done ? (
        <div className="text-center py-4" data-testid="reset-success">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#34D399]/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-[#34D399]" />
          </div>
          <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-2">Mot de passe mis à jour ! 🌸</h2>
          <p className="text-[#4A5568]">Redirection vers la page de connexion…</p>
        </div>
      ) : !token ? (
        <div className="text-center py-4">
          <p className="text-[#4A5568] mb-4">Le lien de réinitialisation est invalide ou incomplet.</p>
          <Link to="/forgot-password" className="text-[#E94A3F] font-bold hover:underline">
            Demander un nouveau lien →
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Choisis un nouveau mot de passe pour ton compte (6 caractères minimum).
          </p>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Nouveau mot de passe</label>
            <PasswordField
              value={password}
              onChange={setPassword}
              minLength={6}
              testid="reset-password-input"
              autoComplete="new-password"
              withIcon
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A2A40] mb-2">Confirmer</label>
            <PasswordField
              value={confirm}
              onChange={setConfirm}
              minLength={6}
              testid="reset-confirm-input"
              autoComplete="new-password"
              withIcon
            />
          </div>
          <button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="w-full bg-[#E94A3F] text-white font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? "…" : (<>Mettre à jour <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      )}
    </AuthShell>
  );
};
